import { CustomField } from '@/api/interface/custom-field';
import useCustomFields from "./api-hooks/useCustomFields"
import { useTypedSelector } from "../store"
import { useMemo } from 'react';
import { transformCFsForSettings, orderByKeys, areObjectsIdentical } from '@/components/pages/manage/self-check-in/utils';
import { SelfCheckIn } from '@/api/interface/self-check-in';
import useSelectedLocation from './api-selected-hooks/useSelectedLocation';

/**
 * 
 * TODO - use unit tested utils for syncing from @/utils/sci-utils.ts.
 * Move helpers to utils file to be reused.
 */

const useSciCustomFields = (selfCheckIns: Array<SelfCheckIn>) => {
  const { hasOnlyOneLocation, isUsingSharedSettings } = useTypedSelector(state => state.pages.manage)
  const { businessId } = useTypedSelector(state => state.selected)
  const { isUsingBothQueueSelectors } = useTypedSelector((state) => state.queueSelect)
  const { customFields, isValidating: isCfValidating } = useCustomFields()
  const { locationId } = useSelectedLocation()

  const shouldUseSharedSettings = useMemo(() => hasOnlyOneLocation || isUsingSharedSettings, 
  [hasOnlyOneLocation, isUsingSharedSettings])

  /**
   * This logic is all tied to getting CFs by SCI
   * If we want to get CFs by other object (location)
   * Extract this and allow for plugging in the object specific logic 
   */

  /* Helpers for getting possible CF */
  /* *********************************** */
  const isCFPossible = (cf: CustomField) => {
    if (
      cf.cfableId === businessId
      && (cf.visibility === 'all' || cf.visibility === 'self_check_in')
    ) {
      return true
    }
    if (
      cf.cfableId === locationId && 
      !(isUsingSharedSettings || hasOnlyOneLocation)
      && (cf.visibility === 'all' || cf.visibility === 'self_check_in')
    ) {
      return true
    }
    return false
  }

  const getPossibleCFs = (customFields: Array<CustomField>, isCFPossible: Function) => {
    return customFields.filter(cf => isCFPossible(cf))
  }
  /* end helpers for getting possible CFs */
  /* *********************************** */

  /* Get CFs: shared, location and settings CFs */
  /* *********************************** */

  const sharedSci = useMemo(() => {
    return selfCheckIns.find(cf => cf.sciableType === 'business')
  }, [selfCheckIns])

  const locationSci = useMemo(() => {
    return selfCheckIns.find(cf => cf.sciableId === locationId)
  }, [selfCheckIns, locationId])

  const settingsCFs = useMemo(() => {
    if (shouldUseSharedSettings && sharedSci) {
      return sharedSci?.settings?.customFields
    }

    if (!shouldUseSharedSettings && locationSci) {
      return { ...locationSci?.settings?.customFields }
    }

    return {}
  }, [shouldUseSharedSettings, sharedSci, locationSci])

  /** 
   * End SCI specifi logic
   * */

  const sharedCFs = useMemo(
    () => customFields?.filter((x) => x.cfableType === "business"),
    [customFields, isUsingSharedSettings]
  )
  
  const locationCFs = useMemo(
    () => customFields?.filter((x) => x.cfableId === locationId),
    [customFields, locationId]
  )
  
  // @TODO: possibleCFs shouldn't all be automatically true
  // otherwise we add CFs prematurely?
  // check it
  const possibleCFs = useMemo(() => {
    if (shouldUseSharedSettings && sharedCFs) {
      return getPossibleCFs(sharedCFs, isCFPossible) 
    }
    if (!shouldUseSharedSettings) {
      return getPossibleCFs([...sharedCFs, ...locationCFs], isCFPossible) 
    }
  }, [shouldUseSharedSettings, sharedCFs])
  /* end get CFs */
  /* *********************************** */

  // Merge transformed viewCFs into current settings CFs
  const syncedCFSettings = useMemo(() => {
    const combinedCFs = transformCFsForSettings(possibleCFs)
    
    return { ...combinedCFs, ...settingsCFs }
  }, [possibleCFs, settingsCFs])
  
  // if viewCFs are different from settingsCF, may need to update the sci to match before editing
  // remove if we do that sync somewhere else
  // if sync is needed, dispatch syncSciSettings action
  const shouldSyncSettingsCFs = useMemo(() => {
    if (!isUsingBothQueueSelectors && settingsCFs && syncedCFSettings) {
      return !areObjectsIdentical(orderByKeys(settingsCFs), orderByKeys(syncedCFSettings))
    }
    return false
  }, [settingsCFs, syncedCFSettings])

  return {
    customFields,
    sharedCFs,
    locationCFs,
    possibleCFs,
    settingsCFs,
    syncedCFSettings,
    shouldSyncSettingsCFs,
    isCfValidating,
  }
}

export default useSciCustomFields